import React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Gift, Copy, Check, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const GiftSuccess = () => {
  const [params] = useSearchParams();
  const code = params.get('code') || '';
  const [copied, setCopied] = React.useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Code gekopieerd!');
      setTimeout(() => setCopied(false), 3000);
    } catch { /* ignore */ }
  };

  const shareWhatsApp = () => {
    const text = `Ik heb een cadeau voor je: een premium abonnement op Oma's Verhaaltjes! Gebruik deze code om te activeren: ${code}\n\nActiveer hier: https://omasverhaaltjes.nl/gift?code=${code}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareEmail = () => {
    const subject = "Een cadeau voor jou: Oma's Verhaaltjes Premium!";
    const body = `Hoi!\n\nIk heb een cadeau voor je: een premium abonnement op Oma's Verhaaltjes - magische slaapverhaaltjes voor kinderen.\n\nJe cadeaucode is: ${code}\n\nActiveer hier: https://omasverhaaltjes.nl/gift?code=${code}\n\nVeel leesplezier!`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-background pt-20 md:pt-24 pb-32 px-4 flex items-start justify-center" data-testid="gift-success-page">
      <div className="max-w-lg w-full">
        <Card className="shadow-2xl border-2 border-gold/30">
          <CardContent className="p-8 text-center space-y-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400/20 to-gold/20 flex items-center justify-center mx-auto">
              <Gift className="w-10 h-10 text-gold" />
            </div>

            <div>
              <h1 className="font-display text-3xl text-foreground mb-2">Cadeau gekocht!</h1>
              <p className="text-muted-foreground">Deel de code hieronder met de ontvanger</p>
            </div>

            {/* Gift Code Display */}
            <div
              className="bg-gold/10 border-2 border-dashed border-gold/40 rounded-2xl p-6 cursor-pointer hover:bg-gold/15 transition-colors"
              onClick={copyCode}
              data-testid="gift-code-display"
            >
              <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Cadeaucode</p>
              <p className="font-mono text-2xl font-bold text-foreground tracking-widest">{code}</p>
              <p className="text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1">
                {copied ? <><Check className="w-3 h-3 text-green-500" /> Gekopieerd!</> : <><Copy className="w-3 h-3" /> Klik om te kopieren</>}
              </p>
            </div>

            {/* Share Options */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">Stuur naar de ontvanger:</p>
              <div className="flex gap-3 justify-center">
                <Button
                  data-testid="gift-share-whatsapp"
                  onClick={shareWhatsApp}
                  className="rounded-full bg-green-500 hover:bg-green-600 text-white font-bold flex-1"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 mr-2 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </Button>
                <Button
                  data-testid="gift-share-email"
                  onClick={shareEmail}
                  variant="outline"
                  className="rounded-full font-bold flex-1"
                >
                  <Send className="w-4 h-4 mr-2" />
                  E-mail
                </Button>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <Link to="/">
                <Button variant="ghost" className="rounded-full text-sm">
                  Terug naar de bibliotheek
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GiftSuccess;
